import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { api } from '../contexts/ApiContext';

export const USER_COMMENT_MAX = 4000;

export type PersonalTxComment = {
  id: number;
  household?: string;
  transaction_id?: number;
  user_id?: number;
  author_name: string;
  author_email?: string;
  body: string;
  created_at?: string | null;
  updated_at?: string | null;
  can_edit?: boolean;
};

export type PersonalTxCommentRow = {
  id: number;
  comments?: PersonalTxComment[] | null;
  user_comment?: string | null;
};

export const rowHasComments = (row?: PersonalTxCommentRow | null) =>
  Boolean((row?.comments && row.comments.length > 0) || (row?.user_comment || '').trim());

export const commentSearchText = (row?: PersonalTxCommentRow | null) =>
  [
    row?.user_comment || '',
    ...(row?.comments || []).flatMap((item) => [item.body, item.author_name]),
  ].join(' ');

export const commentCsvValue = (row?: PersonalTxCommentRow | null) => {
  const comments = row?.comments || [];
  if (comments.length) {
    return comments.map((item) => `${item.author_name}: ${item.body}`).join(' | ');
  }
  return row?.user_comment || '';
};

const formatCommentWhen = (value?: string | null) => {
  if (!value) return '';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleString('en-IE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

type CommentPatch = {
  comments: PersonalTxComment[];
  user_comment: string;
};

export const HouseholdTxCommentThread: React.FC<{
  household: 'seco' | 'omhe';
  rowId: number;
  comments?: PersonalTxComment[] | null;
  testIdPrefix: string;
  autoFocus?: boolean;
  onTransaction: (patch: CommentPatch) => void;
  onError?: (message: string) => void;
}> = ({ household, rowId, comments, testIdPrefix, autoFocus, onTransaction, onError }) => {
  const items = comments || [];
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const composerRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    setDraft('');
    setEditingId(null);
    setEditDraft('');
  }, [rowId]);

  useEffect(() => {
    if (!autoFocus) return;
    const el = composerRef.current;
    if (!el) return;
    el.focus();
  }, [autoFocus, rowId]);

  const applyPayload = (data: { transaction?: PersonalTxCommentRow; comments?: PersonalTxComment[] }) => {
    const commentsNext = data.comments || data.transaction?.comments || [];
    onTransaction({
      comments: commentsNext,
      user_comment: String(data.transaction?.user_comment || ''),
    });
  };

  const addComment = async () => {
    const body = draft.trim();
    if (!body || saving) return;
    setSaving(true);
    try {
      const response = await api.post(`/api/personal-finances/${household}/${rowId}/comments`, { body });
      applyPayload(response.data);
      setDraft('');
    } catch (err: any) {
      onError?.(err.response?.data?.error || 'Failed to add comment');
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (commentId: number) => {
    const body = editDraft.trim();
    if (!body || saving) return;
    setSaving(true);
    try {
      const response = await api.patch(`/api/personal-finances/${household}/comments/${commentId}`, { body });
      applyPayload(response.data);
      setEditingId(null);
      setEditDraft('');
    } catch (err: any) {
      onError?.(err.response?.data?.error || 'Failed to update comment');
    } finally {
      setSaving(false);
    }
  };

  const removeComment = async (commentId: number) => {
    setSaving(true);
    try {
      const response = await api.delete(`/api/personal-finances/${household}/comments/${commentId}`);
      applyPayload(response.data);
      if (editingId === commentId) {
        setEditingId(null);
        setEditDraft('');
      }
    } catch (err: any) {
      onError?.(err.response?.data?.error || 'Failed to delete comment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ py: 1 }} data-testid={`${testIdPrefix}-comments`}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Comments
      </Typography>
      <Stack spacing={1.25} sx={{ mb: 1.5 }}>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No comments yet.
          </Typography>
        ) : (
          items.map((item) => (
            <Box
              key={item.id}
              data-testid={`${testIdPrefix}-comment-item-${item.id}`}
              sx={{
                p: 1,
                borderRadius: 1,
                bgcolor: 'action.hover',
              }}
            >
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
                    {item.author_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCommentWhen(item.updated_at || item.created_at)}
                  </Typography>
                </Box>
                {item.can_edit ? (
                  <Stack direction="row" spacing={0}>
                    <Tooltip title="Edit comment">
                      <IconButton
                        size="small"
                        aria-label={`Edit comment ${item.id}`}
                        data-testid={`${testIdPrefix}-comment-edit-${item.id}`}
                        onClick={() => {
                          setEditingId(item.id);
                          setEditDraft(item.body);
                        }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete comment">
                      <IconButton
                        size="small"
                        aria-label={`Delete comment ${item.id}`}
                        data-testid={`${testIdPrefix}-comment-delete-${item.id}`}
                        onClick={() => void removeComment(item.id)}
                        disabled={saving}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : null}
              </Stack>
              {editingId === item.id ? (
                <Box sx={{ mt: 1 }}>
                  <TextField
                    multiline
                    minRows={2}
                    fullWidth
                    size="small"
                    value={editDraft}
                    onChange={(event) => setEditDraft(event.target.value)}
                    inputProps={{
                      maxLength: USER_COMMENT_MAX,
                      'data-testid': `${testIdPrefix}-comment-edit-field-${item.id}`,
                    }}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      data-testid={`${testIdPrefix}-comment-save-${item.id}`}
                      onClick={() => void saveEdit(item.id)}
                      disabled={saving || !editDraft.trim()}
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setEditingId(null);
                        setEditDraft('');
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                  {item.body}
                </Typography>
              )}
            </Box>
          ))
        )}
      </Stack>
      <TextField
        inputRef={composerRef}
        multiline
        minRows={3}
        fullWidth
        size="small"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Add a comment"
        inputProps={{
          maxLength: USER_COMMENT_MAX,
          'data-testid': `${testIdPrefix}-comment-field`,
          'aria-label': 'Add a transaction comment',
        }}
      />
      <Button
        sx={{ mt: 1 }}
        size="small"
        variant="contained"
        data-testid={`${testIdPrefix}-comment-add`}
        onClick={() => void addComment()}
        disabled={saving || !draft.trim()}
      >
        Add comment
      </Button>
    </Box>
  );
};
